import { useId, type ReactElement } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { CheckboxGroupProps } from './types';

import { typedMemo } from '../../utils/typedMemo';
import { Checkbox } from '../Checkbox/Checkbox';
import { Layout } from '../Layout/Layout';
import { isOptionSelected, optionInputValue, toggleOptionValue } from './utils';
import './CheckboxGroup.css';

const messages = defineMessages({
  requiredGroup: {
    id: 'CheckboxGroup.requiredGroup',
    defaultMessage: 'required',
    description:
      "Appended to a checkbox group's accessible name when a selection is required. `aria-required` is not valid on role=group, so the state is carried in the name instead.",
  },
});

function CheckboxGroupComponent<V = string>({
  label,
  name,
  options,
  value,
  onChange,
  disabled,
  required,
  __private,
}: CheckboxGroupProps<V>) {
  const { ariaDescribedBy, ariaLabelledBy, id } = __private ?? {};
  const intl = useIntl();
  const generatedId = useId();
  const labelId = `${generatedId}-label`;
  const requiredId = `${generatedId}-required`;
  const accessibleName = ariaLabelledBy || required ? undefined : label;
  const accessibleNameIds = required
    ? `${ariaLabelledBy ?? labelId} ${requiredId}`
    : ariaLabelledBy;

  return (
    <Layout
      aria-describedby={ariaDescribedBy}
      aria-label={accessibleName}
      aria-labelledby={accessibleNameIds}
      className="eds-checkbox-group"
      direction="column"
      id={id}
      role="group"
      spacing="s"
    >
      {/* Announced by screen readers when `required` is set. Not visible on screen.
       * Needed because `aria-required` is invalid on role=group. */}
      {required ? (
        <>
          {ariaLabelledBy ? null : (
            <span hidden id={labelId}>
              {label}
            </span>
          )}
          <span hidden id={requiredId}>
            {intl.formatMessage(messages.requiredGroup)}
          </span>
        </>
      ) : null}
      {options.map((option, index) => (
        <Checkbox
          key={optionInputValue(option.value, index)}
          disabled={disabled || option.disabled}
          hint={option.hint}
          label={option.label ?? option.text}
          name={name}
          required={option.required}
          selected={isOptionSelected(value, option.value)}
          status={option.status}
          text={option.text}
          value={optionInputValue(option.value, index)}
          onChange={(selected) => {
            onChange(toggleOptionValue(value, option.value, selected));
          }}
        />
      ))}
    </Layout>
  );
}

export const CheckboxGroup: {
  <V = string>(props: CheckboxGroupProps<V>): ReactElement;
  displayName?: string;
} = typedMemo(CheckboxGroupComponent);

CheckboxGroup.displayName = 'CheckboxGroup';
